import React, { useMemo, useState } from "react";
import logo from "assets/images/login-image.png";
import Input from "components/common/Input";
import Button from "components/common/Button";
import api from "config/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { setUserData } from "store/slices/main.slice";
import GoogleAddress from "components/common/GoogleAddress";
import { IconButton } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useCachedImage } from "hooks/useCachedImage";
import { resolveUploadAssetUrl } from "utils/media";

const Register = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const companyData = useAppSelector(({ main }) => main.companyData);
  const settings = useAppSelector(({ main }) => main.settings);
  const name = useAppSelector(
    ({ main }) => main.companyData?.companyName || "RANGER POS"
  );
  const resolvedLogoUrl = useMemo(
    () =>
      settings?.showLogo !== 0 && companyData?.logoUrl
        ? resolveUploadAssetUrl(companyData.logoUrl)
        : logo,
    [companyData?.logoUrl, settings?.showLogo]
  );
  const logoUrl = useCachedImage(resolvedLogoUrl, { cacheKey: "company-logo" });
  const [data, setData] = useState({
    confirmPassword: "",
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    phoneNo: "",
    address: "",
    address1: "",
  });

  const handleSubmit = () => {
    toast.dismiss();
    if (!data.firstName || !data.lastName) {
      toast.error("First Name & Last Name is required");
      return;
    }

    if (!/^\d{10}$/.test(data.phoneNo)) {
      toast.error("Invalid Phone Number");
      return;
    }
    if (
      !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        data.email,
      )
    ) {
      toast.error("Invalid Email");
      return;
    }

    if (data.password.length <= 3) {
      toast.error("Password must be greater than 3 characters");
      return;
    }

    if (data.password !== data.confirmPassword) {
      toast.error("Confirm password not match");
      return;
    }
    setLoading(true);
    api
      .post(`/api/Customer`, data)
      .then(({ data }) => {
        dispatch(setUserData(data));
        toast.success("User created Successfully");
        navigate("/login");
      })
      .catch((e) => {
        toast.error(e || "Something went wrong");
      })
      .finally(() => setLoading(false));
  };

  const fieldSx = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      backgroundColor: "rgba(255,255,255,0.78)",
    },
  };

  const addressClass =
    "[&_.MuiOutlinedInput-root]:!rounded-[14px] [&_.MuiOutlinedInput-root]:!bg-white/80";

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center px-4 py-6">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.34),_transparent_42%),linear-gradient(135deg,rgba(255,255,255,0.12),transparent_55%)]" />

      <div
        className="relative w-full max-w-6xl overflow-hidden rounded-[30px] border border-white/55 shadow-[0_28px_80px_rgba(15,23,42,0.18)]"
        style={{
          background:
            "linear-gradient(135deg, rgb(255 255 255 / 0.92), rgb(255 255 255 / 0.84))",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          fontFamily: '"Noto Sans", sans-serif',
        }}
      >
        <div className="grid lg:grid-cols-[1.02fr_0.98fr]">
          <div className="relative hidden min-h-[620px] items-center justify-center px-10 py-10 lg:flex">
            <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(255,255,255,0.28),rgba(var(--color-primary),0.08))]" />
            <IconButton
              onClick={() => navigate("/")}
              className="!absolute !left-6 !top-6 !bg-white/80"
            >
              <ArrowBack />
            </IconButton>
            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="rounded-[28px] bg-white/70 px-6 py-6 shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
                <img
                  key={logoUrl}
                  src={logoUrl}
                  className="h-[220px] w-[220px] object-contain lg:h-[280px] lg:w-[280px]"
                  alt="logo"
                />
              </div>
              <div className="mt-8 max-w-[390px]">
                <p className="text-[13px] font-[500] uppercase tracking-[0.12em] text-primary/80">
                  New Account
                </p>
                <h2 className="mt-3 text-[34px] font-[650] leading-[1.08] tracking-[0] text-slate-900">
                  Create your account and checkout faster.
                </h2>
                <p className="mt-3 text-[15px] leading-6 text-slate-600">
                  Save your contact details and addresses for future orders with {name}.
                </p>
              </div>
            </div>
          </div>

          <div className="relative px-5 py-6 sm:px-8 lg:px-10 lg:py-10">
            <div className="lg:hidden mb-4 flex items-center justify-between">
              <IconButton onClick={() => navigate("/")} className="!bg-white/80">
                <ArrowBack />
              </IconButton>
              <img key={logoUrl} src={logoUrl} className="h-14 object-contain" alt="logo" />
              <div className="w-10" />
            </div>

            <div className="mx-auto w-full max-w-[640px]">
              <div className="rounded-[24px] border border-white/65 bg-white/76 p-5 shadow-[0_18px_42px_rgba(15,23,42,0.08)] sm:p-6">
                <p className="text-[12px] font-[500] uppercase tracking-[0.12em] text-primary/80">
                  Account Setup
                </p>
                <h1 className="mt-2 text-[30px] font-[650] leading-[1.08] tracking-[0] text-slate-900">
                  Create new account
                </h1>
                <p className="mt-3 text-[14px] leading-6 text-slate-600">
                  Enter your details once and make future orders easier.
                </p>

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    size="small"
                    label="First Name"
                    variant="outlined"
                    value={data.firstName}
                    sx={fieldSx}
                    onValueChange={(firstName) =>
                      setData((e) => ({ ...e, firstName }))
                    }
                  />
                  <Input
                    size="small"
                    label="Last Name"
                    variant="outlined"
                    value={data.lastName}
                    sx={fieldSx}
                    onValueChange={(lastName) =>
                      setData((e) => ({ ...e, lastName }))
                    }
                  />
                  <Input
                    size="small"
                    label="Phone Number"
                    variant="outlined"
                    value={data.phoneNo}
                    sx={fieldSx}
                    onValueChange={(phoneNo) =>
                      setData((e) => ({ ...e, phoneNo }))
                    }
                  />
                  <Input
                    size="small"
                    label="Email"
                    variant="outlined"
                    value={data.email}
                    sx={fieldSx}
                    onValueChange={(email) => setData((e) => ({ ...e, email }))}
                  />
                  <Input
                    size="small"
                    label="Password"
                    type="password"
                    variant="outlined"
                    value={data.password}
                    sx={fieldSx}
                    onValueChange={(password) =>
                      setData((e) => ({ ...e, password }))
                    }
                  />
                  <Input
                    size="small"
                    label="Confirm Password"
                    type="password"
                    variant="outlined"
                    value={data.confirmPassword}
                    sx={fieldSx}
                    onValueChange={(confirmPassword) =>
                      setData((e) => ({ ...e, confirmPassword }))
                    }
                  />
                  <GoogleAddress
                    className={`sm:col-span-2 ${addressClass}`}
                    lable="Main Address"
                    value={data.address}
                    onChange={(address) => setData((e) => ({ ...e, address }))}
                  />
                  <GoogleAddress
                    className={`sm:col-span-2 ${addressClass}`}
                    lable="Secondary Address"
                    value={data.address1}
                    onChange={(address1) => setData((e) => ({ ...e, address1 }))}
                  />
                </div>

                <Button
                  disabled={loading}
                  className="!mt-6 !w-full !rounded-[16px] !py-3.5 !text-[15px] !font-[600]"
                  onClick={handleSubmit}
                >
                  Register
                </Button>

                <p className="mt-5 text-center text-[14px] text-slate-600">
                  Already have an account?{" "}
                  <a href="/login" className="font-[600] text-primary">
                    Sign in
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
